import React, { useState, useEffect } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useApi } from '@/context/AuthContext';
import { formatIndianCurrency, formatDate, EXPENSE_CATEGORIES, GST_RATES, getStatusClasses, toLocalISODate, getErrorMessage } from '@/utils/helpers';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { Plus, Search, Trash2, Receipt, CalendarIcon, Camera, MessageSquare } from 'lucide-react';

export default function ExpensesPage() {
  const api = useApi();
  const [expenses, setExpenses] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    vendor_id: '',
    vendor_name: '',
    transaction_date: toLocalISODate(),
    amount: '',
    tax_amount: 0,
    description: '',
    category: '',
    gst_rate: 18,
    payment_method: 'upi',
    is_igst: false
  });

  useEffect(() => {
    fetchExpenses();
    fetchVendors();
  }, []);

  const fetchExpenses = async () => {
    try {
      const response = await api.get('/expenses');
      setExpenses(response.data);
    } catch (error) {
      toast.error('Failed to load expenses');
    } finally {
      setLoading(false);
    }
  };

  const fetchVendors = async () => {
    try {
      const response = await api.get('/vendors');
      setVendors(response.data);
    } catch (error) {
      console.error('Error fetching vendors:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      vendor_id: '',
      vendor_name: '',
      transaction_date: toLocalISODate(),
      amount: '',
      tax_amount: 0,
      description: '',
      category: '',
      gst_rate: 18,
      payment_method: 'upi',
      is_igst: false
    });
  };

  const calculateTax = () => {
    const tax = (formData.amount * formData.gst_rate) / 100;
    setFormData(prev => ({ ...prev, tax_amount: Math.round(tax * 100) / 100 }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.description.trim()) {
      toast.error('Description is required');
      return;
    }
    
    if (!(parseFloat(formData.amount) > 0)) {
      toast.error('Amount must be greater than 0');
      return;
    }

    try {
      await api.post('/expenses', { ...formData, amount: parseFloat(formData.amount) });
      toast.success('Expense recorded');
      fetchExpenses();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save expense'));
    }
  };

  const handleDelete = async (expenseId) => {
    if (!window.confirm('Are you sure you want to delete this expense?')) return;
    try {
      await api.delete(`/expenses/${expenseId}`);
      toast.success('Expense deleted');
      fetchExpenses();
    } catch (error) {
      toast.error('Failed to delete expense');
    }
  };

  const filteredExpenses = expenses.filter(e => 
    e.description?.toLowerCase().includes(search.toLowerCase()) ||
    e.vendor_name?.toLowerCase().includes(search.toLowerCase()) ||
    e.category?.toLowerCase().includes(search.toLowerCase())
  );

  const totalExpenses = filteredExpenses.reduce((sum, e) => sum + e.amount + e.tax_amount, 0);
  const totalITC = filteredExpenses.filter(e => e.itc_eligible).reduce((sum, e) => sum + e.tax_amount, 0);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="animate-pulse space-y-4">
          <div className="h-10 bg-stone-200 rounded w-1/3"></div>
          <div className="h-64 bg-stone-200 rounded"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="expenses-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-stone-900 font-['Outfit']">Expenses</h1>
            <p className="text-stone-600">Track and manage your business expenses</p>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" className="gap-2" disabled title="Coming soon" data-testid="scan-receipt-btn">
              <Camera className="w-4 h-4" /> Scan Receipt <span className="text-[10px] uppercase text-[#b8740f]">Soon</span>
            </Button>
            <Button variant="outline" className="gap-2" disabled title="Coming soon" data-testid="expense-whatsapp-btn">
              <MessageSquare className="w-4 h-4" /> WhatsApp <span className="text-[10px] uppercase text-[#b8740f]">Soon</span>
            </Button>
            <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
              <DialogTrigger asChild>
                <Button className="bg-[#1D9E75] hover:bg-[#178a65] text-white btn-press gap-2" data-testid="add-expense-btn">
                  <Plus className="w-4 h-4" /> Add Expense
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle className="font-['Outfit']">Record Expense</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4" data-testid="expense-form">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <Label>Description *</Label>
                      <Textarea
                        value={formData.description}
                        onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                        placeholder="What was this expense for?"
                        required
                        data-testid="expense-description"
                      />
                    </div>
                    <div>
                      <Label>Vendor</Label>
                      <Select 
                        value={formData.vendor_id} 
                        onValueChange={(v) => {
                          const vendor = vendors.find(ven => ven.id === v);
                          setFormData(prev => ({ 
                            ...prev, 
                            vendor_id: v,
                            vendor_name: vendor?.name || ''
                          }));
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select vendor" />
                        </SelectTrigger>
                        <SelectContent>
                          {vendors.map(vendor => (
                            <SelectItem key={vendor.id} value={vendor.id}>
                              {vendor.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Category</Label>
                      <Select 
                        value={formData.category} 
                        onValueChange={(v) => setFormData(prev => ({ ...prev, category: v }))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                        <SelectContent>
                          {EXPENSE_CATEGORIES.map(cat => (
                            <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Date</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="outline" className="w-full justify-start text-left font-normal">
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {formData.transaction_date ? format(new Date(formData.transaction_date), 'dd/MM/yyyy') : 'Pick a date'}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0">
                          <Calendar
                            mode="single"
                            selected={formData.transaction_date ? new Date(formData.transaction_date) : undefined}
                            onSelect={(date) => setFormData(prev => ({ 
                              ...prev, 
                              transaction_date: date ? toLocalISODate(date) : '' 
                            }))}
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                    <div>
                      <Label>Payment Method</Label>
                      <Select 
                        value={formData.payment_method} 
                        onValueChange={(v) => setFormData(prev => ({ ...prev, payment_method: v }))}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="upi">UPI</SelectItem>
                          <SelectItem value="neft">NEFT/RTGS</SelectItem>
                          <SelectItem value="cash">Cash</SelectItem>
                          <SelectItem value="card">Card</SelectItem>
                          <SelectItem value="cheque">Cheque</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Amount (₹) *</Label>
                      <Input
                        type="number"
                        value={formData.amount}
                        onChange={(e) => {
                          const amount = parseFloat(e.target.value) || 0;
                          setFormData(prev => ({ 
                            ...prev, 
                            amount: e.target.value,
                            tax_amount: Math.round((amount * prev.gst_rate / 100) * 100) / 100
                          }));
                        }}
                        min="0"
                        step="0.01"
                        className="font-mono"
                        required
                        data-testid="expense-amount"
                      />
                    </div>
                    <div>
                      <Label>GST Rate (%)</Label>
                      <Select 
                        value={formData.gst_rate.toString()} 
                        onValueChange={(v) => {
                          const rate = parseFloat(v);
                          setFormData(prev => ({ 
                            ...prev, 
                            gst_rate: rate,
                            tax_amount: Math.round(((parseFloat(prev.amount) || 0) * rate / 100) * 100) / 100
                          }));
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {GST_RATES.map(rate => (
                            <SelectItem key={rate} value={rate.toString()}>{rate}%</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>GST Amount (₹)</Label>
                      <Input
                        type="number"
                        value={formData.tax_amount}
                        onChange={(e) => setFormData(prev => ({ ...prev, tax_amount: parseFloat(e.target.value) || 0 }))}
                        min="0"
                        step="0.01"
                        className="font-mono"
                      />
                    </div>
                    <div>
                      <Label>Total</Label>
                      <div className="h-10 bg-stone-100 rounded-md flex items-center px-3 font-mono font-semibold">
                        {formatIndianCurrency((parseFloat(formData.amount) || 0) + (parseFloat(formData.tax_amount) || 0))}
                      </div>
                    </div>
                  </div>
                  <div className="flex justify-end gap-3 pt-4">
                    <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>
                      Cancel
                    </Button>
                    <Button type="submit" className="bg-[#1D9E75] hover:bg-[#178a65] text-white" data-testid="save-expense-btn">
                      Record Expense
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-lg border border-stone-200 p-6">
            <p className="text-sm text-stone-500">Total Expenses</p>
            <p className="text-2xl font-bold font-mono text-stone-900">{formatIndianCurrency(totalExpenses)}</p>
          </div>
          <div className="bg-white rounded-lg border border-stone-200 p-6">
            <p className="text-sm text-stone-500">ITC Eligible</p>
            <p className="text-2xl font-bold font-mono text-green-600">{formatIndianCurrency(totalITC)}</p>
          </div>
          <div className="bg-white rounded-lg border border-stone-200 p-6">
            <p className="text-sm text-stone-500">Expense Count</p>
            <p className="text-2xl font-bold font-mono text-stone-900">{filteredExpenses.length}</p>
          </div>
        </div>

        {/* Search */}
        <div className="flex items-center gap-4">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
            <Input
              placeholder="Search expenses..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {/* Table */}
        {filteredExpenses.length > 0 ? (
          <div className="bg-white rounded-lg border border-stone-200 overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Vendor</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>ITC</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredExpenses.map((expense) => (
                  <TableRow key={expense.id}>
                    <TableCell>{formatDate(expense.transaction_date)}</TableCell>
                    <TableCell className="max-w-xs truncate">{expense.description}</TableCell>
                    <TableCell>{expense.vendor_name || '-'}</TableCell>
                    <TableCell>{expense.category || '-'}</TableCell>
                    <TableCell className="text-right font-mono font-semibold">
                      {formatIndianCurrency(expense.amount + expense.tax_amount)}
                    </TableCell>
                    <TableCell>
                      {expense.itc_eligible ? (
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">Eligible</span>
                      ) : (
                        <span className="text-xs bg-stone-100 text-stone-500 px-2 py-1 rounded">No</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => handleDelete(expense.id)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-stone-200 p-12">
            <div className="text-center">
              <Receipt className="w-16 h-16 text-stone-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-stone-700 mb-2">No expenses yet</h3>
              <p className="text-stone-500 mb-6">Start tracking your business expenses</p>
              <Button onClick={() => setDialogOpen(true)} className="bg-[#1D9E75] hover:bg-[#178a65] text-white gap-2">
                <Plus className="w-4 h-4" /> Add Expense
              </Button>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
